import { useState } from 'react'
import { Dayjs } from 'dayjs'

import Styles from './ConfirmResumeModal.module.scss'

import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Modal, useModals } from 'almanac-ui/Modal'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { Radio } from 'components/RadioGroup/Radio'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { ArrowCurveLeftRight } from 'blaze-ui/icons'

type ResumeAt = 'this-week' | 'next-week'

export const ConfirmResumeModal = ({
  variant,
  startDate,
  onConfirm,
}: {
  variant: 'generate' | 'publish' | 'all'
  startDate: Dayjs
  onConfirm: (resumeAt: ResumeAt) => void
}) => {
  const { closeModal } = useModals()
  const [value, setValue] = useState<ResumeAt>('this-week')

  let header = 'When do you want to resume Autopilot?'

  if (variant === 'generate') {
    header = 'When do you want to resume content generation?'
  } else if (variant === 'publish') {
    header = 'When do you want to resume publishing?'
  }

  return (
    <Modal.Root onPressOutside={closeModal}>
      <Modal.Header title={header} isConfirmation />
      <Modal.Content isConfirmation>
        <div className={Styles.resumeModalBody}>
          <RadioGroup className={Styles.radioGroup} value={value} onChange={(value) => setValue(value as ResumeAt)}>
            <Radio className={Styles.radioOption} value="this-week" hideRadio aria-label={'Resume this week'}>
              {(radioBulletProps) => (
                <>
                  <RadioBullet {...radioBulletProps} size={20} />
                  <div className={Styles.thisWeekOption}>
                    <Heading level={4}>This week</Heading>
                    <Text color="var(--dark-60)">Starting immediately</Text>
                  </div>
                </>
              )}
            </Radio>
            <Radio className={Styles.radioOption} value="next-week" hideRadio aria-label={'Resume next week'}>
              {(radioBulletProps) => (
                <>
                  <RadioBullet {...radioBulletProps} size={20} />
                  <div className={Styles.nextWeekOption}>
                    <Heading level={4}>Next week</Heading>
                    <Text color="var(--dark-60)">Starting week of {startDate.format('MMMM DD')}</Text>
                  </div>
                </>
              )}
            </Radio>
          </RadioGroup>
          <div className={Styles.educationTextContainer}>
            <ArrowCurveLeftRight color="var(--selected-fill)" />
            {variant === 'all' && (
              <Text variant="secondary" color="var(--dark-80)">
                We’ll generate your content{' '}
                <strong>{value === 'next-week' ? `on ${startDate.format('dddd, MMMM D, ')} ` : 'immediately '}</strong>
                and autoschedule it on your calendar for publishing.
              </Text>
            )}
            {variant === 'generate' && (
              <Text variant="secondary" color="var(--dark-80)">
                We’ll generate your content{' '}
                <strong>{value === 'next-week' ? `on ${startDate.format('dddd, MMMM D')}` : 'immediately'}.</strong>
              </Text>
            )}
            {variant === 'publish' && (
              <Text variant="secondary" color="var(--dark-80)">
                We’ll start auto publishing your content{' '}
                {value === 'next-week' && <strong>on {startDate.format('dddd, MMMM D')}.</strong>}
                {value === 'this-week' && 'all autoscheduled content.'}
              </Text>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer isConfirmation>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="secondary" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton onPress={() => onConfirm(value)}>Confirm</Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
